import {
  HttpClientInterface,
  HttpClientRequestMethods,
  HttpClientRequestParametersInterface,
  HttpClientRequestConfig,
  DefaultResponseModel,
} from "@/types/http";
import axios, {
  Axios,
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { hideLoading, showLoading, toastError } from "./notification";
import docCookies from "./cookie-util";
import { storageUtils } from "./utils";
// import { toastError } from "./notification";

/**
 * "multipart/form-data"
 */
export const CONTENT_TYPE_MULTIPART_FORM_DATA: string = "multipart/form-data";
export const APPLICATION_X_WWW_FORM_URLCODED: string =
  "application/x-www-form-urlencoded";

/**
 * 登录是否过期
 */
export const getIsExpire = (): boolean => {
  const { getItem } = storageUtils;
  const locationToken = getItem("Expire");
  const present = +new Date();
  const expire = +new Date(locationToken ?? "1999-08-03 23:16:41");
  return present > expire;
};
export const getAuthorizationToken = (with_prefix:boolean=true) => {
  const { getItem } =
    process.env.NODE_ENV === "development" ? storageUtils : docCookies;
  const token = getItem("AuthorizationToken");
  if (!token) return null;
  const locationToken = (with_prefix?"Bearer ":'') + token;
  return locationToken;
};
export const getRefreshToken =  (with_prefix:boolean=true)  => {
  const { getItem } =
    process.env.NODE_ENV === "development" ? storageUtils : docCookies;
  const token = getItem("RefreshToken");
  if (!token) return null;
  const locationToken = (with_prefix?"Bearer ":'') + token;
  return locationToken;
};
export const cleanAuthorizationToken = () => {
  const { removeItem } = storageUtils;
  removeItem("AuthorizationToken");
  removeItem("RefreshToken");
};
abstract class HttpClient implements HttpClientInterface {
  get logged(): boolean {
    const token = getAuthorizationToken();
    return !!token;
  }
  private instance: AxiosInstance;
  /**
   * 白名单配置
   * 再白名单内部的api不需要token传入
   */
  protected whiteList: Array<string> = [];
  constructor(config: HttpClientRequestConfig = {}, whiteList?: Array<string>) {
    this.instance = axios.create(config);
    //拦截器设置
    this.instance.interceptors.request.use(
      (config) => this.requestInterceptor(config),
      (err: any) => {
        return Promise.reject(err);
      }
    );
    this.instance.interceptors.response.use(
      this.responseInterceptor,
      this.responseError
    );
    this.whiteList = whiteList ?? this.whiteList;
  }

  protected requestInterceptor(
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig {
    const url = config.url as string;

    //没有在白名单内的api需要token
    const requiresToken: boolean = !this.whiteList.includes(url);

    const token = getAuthorizationToken();
    if (!token) {
      //前往登录页面
    }
    if (requiresToken) {
      config.headers!.Authorization = token;
      // config.headers!.Authorization1 = token;
    }
    return config;
  }
  protected responseInterceptor(res: AxiosResponse): AxiosResponse {
    // console.log("响应拦截", res);
    const data = res.data;
    //如果是文件流，直接返回
    if(data instanceof Blob){
      return res;
    }
    
    if (data?.status !== 200) {
      toastError(data?.message ?? "服务器连接异常，请检查服务器！");
    }
    return data;
  }
  protected responseError(error: AxiosError): Promise<AxiosError> {
    if (error.response) {
      const status = error.response.status;
      const data: DefaultResponseModel = error.response
        .data as DefaultResponseModel;
      let errorMessage = "服务器连接异常，请检查服务器！";
      switch (status) {
        case 400:
          errorMessage = "请求错误";
          break;
        case 401:
          errorMessage = "未授权的访问，请登录";
          break;
        case 403:
          errorMessage = "拒绝访问";
          break;
        case 404:
          errorMessage = "请求的资源不存在";
          break;
        case 500:
          errorMessage = "服务器内部错误";
          break;
        case 502:
          errorMessage = "网关错误";
          break;
        case 504:
          errorMessage = "无网络连接，请检查网络";
          break;
        default:
          errorMessage = "发生错误，请稍后重试";
          break;
      }
      console.log(error.response);
      toastError(data?.message ?? errorMessage);
      // console.error(errorMessage); // 输出错误信息
    } else {
      toastError("服务器连接异常，请检查服务器！");
    }
    // 将错误对象继续向下传递
    return Promise.reject(error.response);
  }
  request<T = DefaultResponseModel>(
    parameters: HttpClientRequestParametersInterface,
    method: HttpClientRequestMethods = "GET"
  ): Promise<T> {
    const response = new Promise<T>((resolve, reject) => {
      const {
        url,
        data,
        params,
        action,
        config: customConfig,
        unprocessed = false,
        disableLoading = false,
        requiresAuth = true,
      } = parameters;
      //如果需要token，且没有登录，则跳放弃该请求
      if (requiresAuth && !this.logged) {
        return Promise.resolve("请登录");
      }

      const config: HttpClientRequestConfig = {
        method,
        url,
        data,
        params,
        action,
        ...customConfig,
        // withCredentials: true,
      };
      if (!disableLoading) showLoading();
      this.instance
        .request(config)
        .then((res) => {
          const data = (res as any as DefaultResponseModel)?.data;
    
          hideLoading();
          if (unprocessed) {
            //不处理直接返回,返回类 [DefaultResponseModel]
            resolve(res as T);
          } else {
            //尝试处理为Model
            //某些数据返回的为string,转换失败直接返回
            // let data=res?.data;
            // if(data?.data)data=data?.data;
            if (typeof data === "object") resolve(data as T);
            try {
              const model: T = JSON.parse(data?.toString());
              resolve(model);
            } catch (error) {
              resolve(data as T);
            }
          }
        })
        .catch((err: AxiosError) => {
          hideLoading();
          // toastError(err?.message || "网络请求错误");
          resolve(err?.message as any);
        });
    });
    return response;
  }
  get<T>(parameters: HttpClientRequestParametersInterface): Promise<T> {
    return this.request<T>(parameters, "GET");
  }
  post<T>(parameters: HttpClientRequestParametersInterface): Promise<T> {
    return this.request<T>(parameters, "POST");
  }
  put<T>(parameters: HttpClientRequestParametersInterface): Promise<T> {
    return this.request<T>(parameters, "PUT");
  }
  option<T>(parameters: HttpClientRequestParametersInterface): Promise<T> {
    return this.request<T>(parameters, "OPTIONS");
  }
  delete<T>(parameters: HttpClientRequestParametersInterface): Promise<T> {
    return this.request<T>(parameters, "DELETE");
  }
  /**
   * 下载接口基于@get 方法实现
   * 只是在 @get 基础上修改 @responseType 等于 "blob"，并在 @responseInterceptor 中判断response.data 为 Blob 类型
   * 如果为Blob 类型，则直接返回Blob
   * 该接口应该总是返回 Promise<Blob>类型
   */
  download<T extends Blob=Blob>(parameters: HttpClientRequestParametersInterface): Promise<T> {
    return this.get<T>({
      ...parameters,
      config:{
        ...parameters.config,
        responseType:"blob"
      }
    });
  }
}

export const download_of=(data:Blob,name:string)=>{
      // 创建一个 Blob 对象，传入数据并指定 MIME 类型
    const blob = new Blob([data], { type: "application/octet-stream" });
    // 创建下载链接
    let link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = name;  // 设置下载的文件名
    // 触发点击事件，下载文件
    document.body.appendChild(link);
    link.click();
    // 移除链接节点
    document.body.removeChild(link);
}

class SimpleHttpClient extends HttpClient {}

const http = new SimpleHttpClient({
  baseURL:
    process.env.NODE_ENV === "development"
      ? "/api"
      : process.env.REACT_APP_HTTP_BASE_URL,
});

export { SimpleHttpClient };

export default http;
